import { useState } from "react";
import { Loader2, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, apiError } from "@/lib/api";

/**
 * A learner's unit, corrected on the portal.
 *
 * The Coursera sheet names a learner's entity in its own words, and for a few officers those words
 * put them in the wrong unit. Corrected here, the correction is kept against the person, so it
 * beats the sheet on every upload that follows.
 */
export const LearnerUnit = ({ learner, units = [], canEdit, onMoved }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const move = async (plant) => {
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/training/learners/${encodeURIComponent(learner.email)}/unit`,
        { plant },
      );
      toast.success(
        data.changed
          ? `${learner.name} moved from ${data.was || "no unit"} to ${plant}. The portal keeps this through the next upload.`
          : `${learner.name} is already on ${plant}.`,
      );
      setOpen(false);
      onMoved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit)
    return (
      <span className="font-data" data-testid={`learner-unit-${learner.email}`}>
        {learner.plant || "—"}
      </span>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`learner-unit-${learner.email}`}
          onClick={(e) => e.stopPropagation()}
          title="Move this officer to the unit they are actually posted at"
          className="flex items-center gap-1.5 border border-dashed border-border px-2 py-1 font-data text-xs transition-colors duration-200 hover:border-primary hover:text-primary"
        >
          {learner.plant || "no unit"}
          {learner.unit_set_by_hand && (
            <span
              className="font-display text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--healthy))]"
              data-testid={`learner-unit-fixed-${learner.email}`}
            >
              set here
            </span>
          )}
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <PencilLine className="h-3 w-3 opacity-60" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onClick={(e) => e.stopPropagation()}
        className="max-h-72 w-60 overflow-y-auto rounded-none bg-popover p-0"
      >
        <div className="border-b border-border px-3 py-2">
          <div className="label-caps">Posted at</div>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            The portal remembers this against {learner.name?.split(" ")[0] || "the officer"}, so the
            next tracker upload cannot put them back.
          </p>
        </div>
        {units.map((u) => (
          <button
            key={u}
            type="button"
            disabled={busy}
            data-testid={`learner-unit-option-${learner.email}-${u}`}
            onClick={() => move(u)}
            className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors duration-200 hover:bg-muted disabled:opacity-40 ${
              u === learner.plant ? "font-bold text-primary" : ""
            }`}
          >
            {u}
            {u === learner.plant && (
              <span className="font-display text-[9px] uppercase tracking-widest">on record</span>
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
